import { MAX_LEVEL, expectedIlvl } from '../content/balance';
import type { Rng } from '../core/rng';
import { CARDS, COMBAT_SIGILS, NEGATIVE_SIGILS, gemItemId, makeStone } from './build';
import {
  type AccessoryItem,
  COMBAT_STATS,
  type CombatStat,
  type EquipItem,
  GEAR_SLOTS,
  type Rarity,
  RARITIES,
  makeGear,
  newUid,
} from './items';

export interface LootDrop {
  items: { id: string; count: number }[];
  equip: EquipItem[];
  silver: number;
  gold: number;
  cards: string[];
}

export interface LootContext {
  /** Content level (monster level / instance level). */
  level: number;
  rng: Rng;
  /** Rested bonus doubles material rewards. */
  rested?: boolean;
}

type Weights = Partial<Record<Rarity, number>>;

interface Entry {
  chance: number;
  item?: string;
  count?: [number, number];
  silver?: [number, number];
  gold?: [number, number];
  gear?: Weights;
  accessory?: Weights;
  stone?: Weights;
  card?: { pool: 'story' | 'colossi' | 'any' | string[] };
  gem?: { type?: 'dmg' | 'cd'; levels: [number, number] };
  /** Scale counts by content level. */
  scale?: boolean;
  materialOnly?: boolean;
}

const e = (chance: number, rest: Omit<Entry, 'chance'>): Entry => ({ chance, ...rest });

const TABLES: Record<string, Entry[]> = {
  vale_beast: [e(1, { silver: [6, 14], scale: true }), e(0.35, { item: 'hide', count: [1, 2] }), e(0.04, { gear: { uncommon: 7, rare: 3 } }), e(0.03, { item: 'potion_small', count: [1, 1] })],
  grubling: [e(1, { silver: [5, 11], scale: true }), e(0.05, { gear: { uncommon: 7, rare: 3 } }), e(0.2, { item: 'relic_fragment', count: [1, 1] }), e(0.03, { item: 'potion_small', count: [1, 1] })],
  bandit: [e(1, { silver: [9, 18], scale: true }), e(0.06, { gear: { uncommon: 6, rare: 4 } }), e(0.03, { item: 'potion_small', count: [1, 2] }), e(0.02, { item: 'grenade_flame', count: [1, 1] })],
  normal: [e(1, { silver: [8, 16], scale: true }), e(0.05, { gear: { uncommon: 6, rare: 4 } }), e(0.03, { item: 'potion_small', count: [1, 1] })],
  demon: [e(1, { silver: [4, 8], scale: true }), e(0.4, { item: 'aether_shard', count: [4, 10] }), e(0.03, { item: 'ore_armor', count: [2, 4] })],
  elite: [e(1, { silver: [40, 70], scale: true }), e(0.3, { gear: { rare: 6, epic: 3, legendary: 0.3 } }), e(0.08, { accessory: { rare: 6, epic: 3 } }), e(0.02, { card: { pool: 'any' } }), e(0.1, { item: 'potion_major', count: [1, 1] })],
  field_boss: [
    e(1, { silver: [300, 500], scale: true }),
    e(1, { gold: [10, 20] }),
    e(1, { gear: { epic: 7, legendary: 2 } }),
    e(0.6, { accessory: { epic: 6, legendary: 2 } }),
    e(0.2, { card: { pool: 'any' } }),
    e(0.35, { item: 'skill_potion', count: [1, 1] }),
    e(1, { item: 'leap_crystal', count: [2, 4] }),
  ],
  // Hidden chests placed in zones and dungeons.
  crypt_chest: [e(1, { silver: [80, 140], scale: true }), e(0.6, { gear: { uncommon: 4, rare: 5, epic: 1 } }), e(0.5, { item: 'relic_fragment', count: [2, 4] }), e(0.3, { item: 'potion_small', count: [2, 3] })],
  tomb_chest: [e(1, { silver: [120, 200], scale: true }), e(0.7, { gear: { rare: 6, epic: 2 } }), e(0.4, { accessory: { rare: 5, epic: 1 } }), e(0.4, { item: 'grenade_flash', count: [1, 2] })],
  tomb_cache: [e(1, { silver: [100, 180], scale: true }), e(1, { item: 'aether_shard', count: [15, 30] }), e(0.4, { gear: { rare: 6, epic: 2 } }), e(0.2, { item: 'relic_rare', count: [1, 1] })],
  keep_chest: [e(1, { silver: [160, 260], scale: true }), e(0.8, { gear: { rare: 5, epic: 3, legendary: 0.2 } }), e(0.4, { accessory: { rare: 5, epic: 2 } }), e(0.1, { card: { pool: 'story' } })],
  oasis_chest: [e(1, { silver: [120, 220], scale: true }), e(0.6, { gear: { rare: 6, epic: 2 } }), e(0.5, { item: 'potion_major', count: [1, 2] }), e(0.3, { item: 'aether_shard', count: [10, 25] })],
  raider_chest: [e(1, { silver: [140, 230], scale: true }), e(1, { gear: { rare: 5, epic: 3 } }), e(0.5, { item: 'grenade_flame', count: [2, 3] }), e(0.25, { accessory: { rare: 6, epic: 2 } })],
  graveyard_chest: [e(1, { silver: [150, 250], scale: true }), e(0.8, { gear: { rare: 5, epic: 3 } }), e(0.4, { item: 'grenade_dark', count: [1, 2] }), e(0.2, { item: 'relic_rare', count: [1, 2] })],
  abbey_chest: [e(1, { silver: [160, 260], scale: true }), e(0.8, { gear: { rare: 5, epic: 3 } }), e(0.5, { item: 'potion_elixir', count: [1, 1] }), e(0.1, { card: { pool: 'story' } })],
  yeti_hoard: [e(1, { silver: [200, 320], scale: true }), e(1, { gear: { rare: 4, epic: 4, legendary: 0.4 } }), e(0.5, { accessory: { epic: 3, legendary: 0.5 } }), e(0.6, { item: 'aether_shard', count: [20, 40] })],
  // Raid gate bosses: tokens for the set exchanges (gate gold and bonus chests come from the instance).
  deepvault_g1: [e(1, { item: 'vault_token', count: [6, 9] }), e(1, { item: 'leap_crystal', count: [4, 7] }), e(0.4, { accessory: { epic: 5, legendary: 1 } })],
  deepvault_g2: [e(1, { item: 'vault_token', count: [9, 13] }), e(1, { item: 'leap_crystal', count: [5, 8] }), e(0.5, { accessory: { epic: 4, legendary: 1.5 } }), e(0.2, { card: { pool: 'any' } })],
  siege_g1: [e(1, { item: 'warlord_relic', count: [4, 6] }), e(1, { item: 'fusion_alloy', count: [2, 4] }), e(0.3, { accessory: { epic: 4, legendary: 1.5 } })],
  siege_g2: [e(1, { item: 'warlord_relic', count: [6, 9] }), e(1, { item: 'fusion_alloy', count: [3, 5] }), e(0.35, { accessory: { epic: 3, legendary: 2 } })],
  siege_g3: [e(1, { item: 'warlord_relic', count: [9, 12] }), e(1, { item: 'fusion_alloy', count: [4, 6] }), e(0.4, { stone: { epic: 4, legendary: 2 } }), e(0.3, { card: { pool: 'any' } })],
  // Colossus kills (the hunt's completion reward is granted separately by the instance).
  colossus_cinderhide: [e(1, { item: 'colossus_mark', count: [8, 12] }), e(1, { item: 'leap_crystal', count: [3, 6] }), e(0.3, { stone: { rare: 6, epic: 3, legendary: 0.5 } }), e(0.25, { card: { pool: 'colossi' } })],
  colossus_gloomfang: [e(1, { item: 'colossus_mark', count: [9, 13] }), e(1, { item: 'leap_crystal', count: [3, 6] }), e(0.3, { stone: { rare: 6, epic: 3, legendary: 0.5 } }), e(0.25, { card: { pool: 'colossi' } })],
  colossus_rimebound: [e(1, { item: 'colossus_mark', count: [10, 14] }), e(1, { item: 'leap_crystal', count: [4, 7] }), e(0.3, { stone: { rare: 6, epic: 3, legendary: 0.5 } }), e(0.25, { card: { pool: 'colossi' } })],
  colossus_dunestrider: [e(1, { item: 'colossus_mark', count: [11, 15] }), e(1, { item: 'leap_crystal', count: [4, 7] }), e(0.35, { stone: { rare: 5, epic: 4, legendary: 0.7 } }), e(0.25, { card: { pool: 'colossi' } })],
  island_chest: [e(1, { silver: [150, 260], scale: true }), e(1, { item: 'aether_shard', count: [20, 40] }), e(0.4, { gear: { rare: 5, epic: 3 } }), e(0.25, { accessory: { rare: 5, epic: 2 } }), e(0.1, { card: { pool: 'any' } })],
  chest: [e(1, { silver: [60, 120], scale: true }), e(0.5, { item: 'potion_small', count: [2, 3] }), e(0.3, { gear: { rare: 6, epic: 2 } }), e(0.2, { item: 'grenade_flash', count: [1, 2] })],
  bandit_chest: [e(1, { silver: [120, 200], scale: true }), e(1, { gear: { rare: 5, epic: 3 } }), e(0.5, { item: 'grenade_flame', count: [2, 3] }), e(0.3, { accessory: { rare: 6, epic: 2 } })],
  story_boss: [
    e(1, { silver: [400, 700], scale: true }),
    e(1, { gear: { epic: 6, legendary: 1 } }),
    e(1, { accessory: { rare: 4, epic: 5 } }),
    e(0.8, { card: { pool: 'story' } }),
    e(1, { item: 'skill_potion', count: [1, 1] }),
    e(0.6, { gem: { levels: [1, 2] } }),
  ],
  // Endgame (research 02 §11.7). Counts scale with the rested bonus.
  rift: [
    e(1, { item: 'aether_shard', count: [450, 550], materialOnly: true }),
    e(1, { item: 'ore_armor', count: [70, 90], materialOnly: true }),
    e(1, { item: 'ore_weapon', count: [22, 28], materialOnly: true }),
    e(1, { item: 'leap_crystal', count: [2, 3], materialOnly: true }),
    e(1, { silver: [2400, 2800] }),
    e(1, { item: 'rift_sigil', count: [10, 14] }),
    e(1, { accessory: { rare: 60, epic: 35, legendary: 5 } }),
    e(0.5, { accessory: { epic: 60, legendary: 35, relic: 5 } }),
    e(0.1, { gem: { levels: [1, 2] } }),
    e(0.04, { card: { pool: 'any' } }),
    e(0.05, { stone: { epic: 8, legendary: 2 } }),
  ],
  colossus: [
    e(1, { item: 'leap_crystal', count: [16, 22], materialOnly: true }),
    e(1, { item: 'ore_armor', count: [60, 80], materialOnly: true }),
    e(1, { item: 'ore_weapon', count: [20, 26], materialOnly: true }),
    e(1, { silver: [1500, 2000] }),
    e(1, { item: 'colossus_mark', count: [10, 14] }),
    e(1, { accessory: { rare: 3, epic: 6, legendary: 1 } }),
    e(1, { accessory: { epic: 6, legendary: 3, relic: 1 } }),
    e(0.08, { card: { pool: 'colossi' } }),
    e(0.08, { gem: { levels: [2, 3] } }),
    e(0.15, { stone: { epic: 6, legendary: 3 } }),
  ],
  deepvault_1: [e(1, { gold: [200, 200] }), e(1, { item: 'vault_token', count: [2, 2] }), e(1, { item: 'leap_crystal', count: [15, 15] }), e(1, { silver: [5000, 5000] }), e(0.5, { accessory: { epic: 1 } })],
  deepvault_2: [e(1, { gold: [300, 300] }), e(1, { item: 'vault_token', count: [3, 3] }), e(1, { item: 'leap_crystal', count: [20, 20] }), e(0.25, { gem: { levels: [2, 2] } }), e(0.15, { accessory: { legendary: 1 } })],
  siege_normal_1: [e(1, { gold: [400, 400] }), e(1, { item: 'warlord_relic', count: [4, 4] }), e(0.3, { accessory: { legendary: 1 } }), e(0.1, { gem: { levels: [3, 3] } }), e(0.25, { item: 'forge_tome', count: [1, 1] })],
  siege_normal_2: [e(1, { gold: [600, 600] }), e(1, { item: 'warlord_relic', count: [5, 5] }), e(0.3, { accessory: { legendary: 1 } }), e(0.1, { gem: { levels: [3, 3] } }), e(0.25, { item: 'forge_tome', count: [1, 1] })],
  siege_normal_3: [e(1, { gold: [900, 900] }), e(1, { item: 'warlord_relic', count: [6, 6] }), e(0.3, { accessory: { legendary: 1 } }), e(0.1, { gem: { levels: [3, 3] } }), e(0.25, { item: 'forge_tome', count: [1, 1] }), e(0.05, { card: { pool: ['c_tyrant'] } })],
  siege_hard_1: [e(1, { gold: [600, 600] }), e(1, { item: 'warlord_relic', count: [6, 6] }), e(0.1, { accessory: { relic: 1 } }), e(0.15, { gem: { levels: [3, 4] } })],
  siege_hard_2: [e(1, { gold: [900, 900] }), e(1, { item: 'warlord_relic', count: [8, 8] }), e(0.1, { accessory: { relic: 1 } }), e(0.15, { gem: { levels: [3, 4] } })],
  siege_hard_3: [e(1, { gold: [1300, 1300] }), e(1, { item: 'warlord_relic', count: [10, 10] }), e(0.1, { accessory: { relic: 1 } }), e(0.2, { gem: { levels: [4, 4] } }), e(0.1, { card: { pool: ['c_tyrant'] } })],
  quest_small: [e(1, { silver: [30, 60], scale: true }), e(0.3, { item: 'potion_small', count: [1, 2] })],
};

// Card pools.
const STORY_CARDS = ['c_brenna', 'c_lyra', 'c_borin', 'c_hadi', 'c_bjorn', 'c_lio', 'c_chieftain', 'c_warden', 'c_matriarch', 'c_zahra', 'c_vira'];
const COLOSSI_CARDS = ['c_cinderhide', 'c_gloomfang', 'c_rimebound', 'c_dunestrider'];

export function hasLootTable(id: string): boolean {
  return id in TABLES;
}

function pickRarity(w: Weights, rng: Rng): Rarity {
  const entries = RARITIES.filter((r) => (w[r] ?? 0) > 0).map((r) => ({ weight: w[r] as number, value: r }));
  return entries.length ? rng.weighted(entries) : 'normal';
}

const RARITY_ILVL_BONUS: Record<Rarity, number> = { normal: 0, uncommon: 2, rare: 4, epic: 8, legendary: 12, relic: 16, ancient: 20, mythic: 24 };

/** Leveling gear for a content level. */
export function rollGear(level: number, rarity: Rarity, rng: Rng): EquipItem {
  const slot = rng.pick(GEAR_SLOTS);
  const ilvl = Math.max(1, Math.round(expectedIlvl(Math.min(level, MAX_LEVEL)) * rng.range(0.92, 1.05) + RARITY_ILVL_BONUS[rarity]));
  return makeGear(slot, Math.min(ilvl, 245), rarity, { quality: Math.round(rng.range(5, 100)) });
}

const ACC_STAT_BASE: Record<Rarity, number> = { normal: 20, uncommon: 40, rare: 70, epic: 110, legendary: 160, relic: 210, ancient: 260, mythic: 300 };
const ACC_SIGIL_PTS: Record<Rarity, [number, number]> = { normal: [0, 0], uncommon: [1, 1], rare: [1, 2], epic: [2, 3], legendary: [3, 4], relic: [3, 5], ancient: [4, 6], mythic: [5, 6] };

export function rollAccessory(level: number, rarity: Rarity, rng: Rng): AccessoryItem {
  const slot = rng.pick(['necklace', 'earring', 'ring'] as const);
  const ilvl = Math.round(expectedIlvl(level));
  const scale = (0.4 + Math.min(level, MAX_LEVEL + 12) / 42) * ACC_STAT_BASE[rarity];
  const stats: Partial<Record<CombatStat, number>> = {};
  const primary: CombatStat[] = ['crit', 'spec', 'swift'];
  const nStats = slot === 'necklace' ? 2 : 1;
  const picks = rng.shuffle([...primary, ...COMBAT_STATS.filter((s) => !primary.includes(s))].slice(0, nStats === 2 ? 4 : 3)).slice(0, nStats);
  for (const s of picks) stats[s] = Math.round(scale * (slot === 'necklace' ? 1.6 : 1) * rng.range(0.85, 1.15));
  const [lo, hi] = ACC_SIGIL_PTS[rarity];
  const sigils: { id: string; pts: number }[] = [];
  if (hi > 0) {
    const two = rng.shuffle([...COMBAT_SIGILS]).slice(0, 2);
    sigils.push({ id: two[0] as string, pts: rng.int(lo, hi) });
    sigils.push({ id: two[1] as string, pts: rng.int(Math.max(1, lo - 1), Math.max(1, hi - 1)) });
    if (rng.chance(0.25)) sigils.push({ id: rng.pick(NEGATIVE_SIGILS), pts: rng.int(1, 3) });
  }
  const names = { necklace: 'Pendant', earring: 'Earring', ring: 'Ring' };
  const prefix = rarity === 'relic' ? 'Hollowglass' : rarity === 'legendary' ? 'Lanternlit' : rarity === 'epic' ? 'Runed' : rarity === 'rare' ? 'Silvered' : 'Simple';
  return { uid: newUid('acc'), kind: 'accessory', slot, name: `${prefix} ${names[slot]}`, rarity, quality: Math.round(rng.range(0, 100)), ilvl, stats, sigils, bound: false };
}

export function rollLoot(tableId: string, ctx: LootContext): LootDrop {
  const out: LootDrop = { items: [], equip: [], silver: 0, gold: 0, cards: [] };
  const table = TABLES[tableId];
  if (!table) return out;
  const { rng } = ctx;
  const levelScale = 1 + Math.min(ctx.level, MAX_LEVEL) * 0.25;
  const restMult = ctx.rested ? 2 : 1;
  for (const en of table) {
    if (!rng.chance(en.chance)) continue;
    if (en.silver) out.silver += Math.round(rng.int(en.silver[0], en.silver[1]) * (en.scale ? levelScale : 1) * restMult);
    if (en.gold) out.gold += rng.int(en.gold[0], en.gold[1]) * restMult;
    if (en.item) {
      const n = en.count ? rng.int(en.count[0], en.count[1]) : 1;
      out.items.push({ id: en.item, count: n * (en.materialOnly || en.item.endsWith('_sigil') || en.item.endsWith('_mark') ? restMult : 1) });
    }
    if (en.gear) out.equip.push(rollGear(ctx.level, pickRarity(en.gear, rng), rng));
    if (en.accessory) out.equip.push(rollAccessory(ctx.level, pickRarity(en.accessory, rng), rng));
    if (en.stone) out.equip.push(makeStone(rng, pickRarity(en.stone, rng)));
    if (en.card) {
      const pool = Array.isArray(en.card.pool) ? en.card.pool : en.card.pool === 'story' ? STORY_CARDS : en.card.pool === 'colossi' ? COLOSSI_CARDS : Object.keys(CARDS);
      out.cards.push(rng.pick(pool));
    }
    if (en.gem) out.items.push({ id: gemItemId(en.gem.type ?? (rng.chance(0.5) ? 'dmg' : 'cd'), rng.int(en.gem.levels[0], en.gem.levels[1])), count: 1 });
  }
  return out;
}
